import {
  BackendError,
  CiviCrmUncontactableError,
  DatabaseUncontactableError,
  ServerUncontactableError,
} from '@/errors';
import ErrorPopin from '@components/ErrorPopin';
import { BACKEND_STATUS_UP, backendStatusAtom } from '@australiangreens/ag-internal-components';
import { Box, Typography } from '@mui/material';
import { getDefaultStore } from 'jotai';
import { ReactNode, useCallback } from 'react';
import { extractErrorDetails } from './errorParsing';

interface ErrorFallbackProps {
  error: unknown;
  componentStack?: ReactNode;
  resetError?: () => void;
  resetErrorBoundary?: () => void;
  includeStack?: boolean;
}

/**
 * Full-page fallback for unexpected React/render crashes.
 * Transient API outages use BackendUnavailableOverlay instead (page stays visible).
 * This screen is only for hard render failures that escaped into the tree.
 */
export default function ErrorFallback({
  error,
  resetError,
  resetErrorBoundary,
  includeStack = false,
}: ErrorFallbackProps) {
  const recover = useCallback(() => {
    getDefaultStore().set(backendStatusAtom, BACKEND_STATUS_UP);

    if (resetError) resetError();
    if (resetErrorBoundary) resetErrorBoundary();
  }, [resetError, resetErrorBoundary]);

  let message = 'Unknown issue';

  if (error instanceof BackendError) {
    message = 'Unknown server issue';
    if (error instanceof ServerUncontactableError) {
      message = 'Unable to contact the backend server';
    } else if (error instanceof DatabaseUncontactableError) {
      message =
        'The backend server is running, but is unable to contact the database it needs to work.';
    } else if (error instanceof CiviCrmUncontactableError) {
      message = 'The backend server is running, but is unable to contact CiviCRM';
    }
  }

  const errorDetails = extractErrorDetails(error, includeStack);

  return (
    <ErrorPopin
      title="An error has occurred in the application"
      message={
        <Box sx={{ overflowWrap: 'anywhere' }}>
          <Typography>{message}</Typography>
          <br />
          <Typography variant="subtitle2">Details</Typography>
          {Object.entries(errorDetails).map(
            ([key, val]) =>
              key !== 'stack' && (
                <Typography variant="body2" key={key}>
                  {key} = {val}
                </Typography>
              )
          )}
          {includeStack && 'stack' in errorDetails && (
            <>
              <br />
              <Typography variant="subtitle2">Stack trace</Typography>
              <pre style={{ fontSize: 'smaller' }}>{errorDetails.stack}</pre>
            </>
          )}
        </Box>
      }
      handler={recover}
      button="Click here to try again"
    />
  );
}
